import { useState, useEffect, useCallback, useRef } from 'react';
import { sandhillsRequest, getURLParameter } from '@Components/Utilities/SandhillsUtility';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import translator from '@Components/Utilities/Translator';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';

// Currently this component will only accept image files.

// Accepted props:
// - media = array of objects
//      [{
//          src: string,
//          mediaId: string,
//          mediaUrl: string,
//          fileName: string,
//          loaded: boolean
//      }]
// - onChange = function()
// - maxPhotoUpload = integer (default: 1)
// - id = string
// - name = string
// - label = string
// - required = boolean (default: false)
// - labelClassName = string (default: 'bold')
// - containerClassName = string
// - inputClassName = string
// - validationError = string
// - requiredSpanClassName = string (default: 'required red')


const SerialNumMedia = (props) => {

    const mediaType = props.mediaType;
    const mediaPurpose = 'Listing Image';
    const waitInterval = 2000;
    const numOfIntervals = 12;
    const waitAfterAvailable = 2000;
    const maxContentLength = 10485760;
    const maxPhotoUpload = 1;
    const fileInput = useRef(null);

    const [media, setMedia] = useState(props.media || []);
    const [mediaError, setMediaError] = useState('');
    const routes = useRecoilValue(atoms.routes);
    const [loading, setLoading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
        if (!props.disabled) {
            setMedia(props.media || []);
        }
        else if (props.disabled) {
            window.removeEventListener('dragover', handleDragOver);
            window.removeEventListener('drop', handleDrop);
        }
    }, [props.media]);

    const loadMedia = (data, count) => {
        if (!data.loaded && !props.disabled) {
            if (count >= numOfIntervals) {
                updateMedia({
                    ...data,
                    src: data.mediaUrl,
                    loaded: true
                });
            } else {
                checkMediaAvailability(data, count);
            }
        }
    };

    const updateMedia = (updatedFile) => {
        if (!props.disabled) {
            setMedia(previousState => {
                const newMedia = [...previousState].map(file => {
                    if (file.mediaId == updatedFile.mediaId) {
                        return updatedFile;
                    } else {
                        return file;
                    }
                });
                props.onChange(newMedia);
                return newMedia;
            });
        }    
    };

    const deleteMedia = (data) => {
        if (!props.disabled) {
            setMedia(previousState => {
                const newMedia = [...previousState].filter(file => file.mediaId != data.mediaId);
                props.onChange(newMedia);
                return newMedia;
            });
        }
    };

    const checkMediaAvailability = (data, count) => {
        if (!props.disabled) {
            let element = document.createElement('a');
            let mediaID = getURLParameter(data.mediaUrl, 'id')
            let listingPub = getURLParameter(data.mediaUrl, 'lp')
            let requestData = {
                sMediaID: mediaID,
                sListingPub: listingPub
            };

            element.href = data.mediaUrl;

            // need to use JSONP here since the media project doesn't handle CORS for us:
            sandhillsRequest({
                url: '//' + element.hostname + routes.IsMediaAvailable,
                data: requestData,
                responseType: 'jsonp',
                errorHandling: {
                    showDetails: true,
                    useConsole: true,
                    useAlert: false
                }
            }).then((response) => {
                if (response == true) {
                    if (count == 1) {
                        updateMedia({
                            ...data,
                            src: data.mediaUrl,
                            loaded: true
                        });
                    } else {
                        setTimeout(() => {
                            updateMedia({
                                ...data,
                                src: data.mediaUrl,
                                loaded: true
                            });
                        }, waitAfterAvailable);
                    }

                    if (props.fireLookup) {
                        let allMedia = [data];
                        props.fireLookup(allMedia);
                        setLoading(false);
                    }
                } else {
                    setTimeout(() => loadMedia(data, count + 1), waitInterval);
                    setLoading(false);
                }
            });
        }
    };

    const onInputChange = (e) => {
        if (!props.disabled) {
            setLoading(true);
            if (e && (e.target && e.target.files) || (e.dataTransfer && e.dataTransfer.files)) {
                let data = new FormData();
                let files = e.target.files ?? e.dataTransfer.files;

                data.append('MediaUploadData', files[0])
                data.append('MediaPurpose', mediaPurpose);
                data.append('MediaType', mediaType);
                data.append('MaxContentLength', maxContentLength);
                data.append('IsSNG', true);

                setMediaError('');

                e.target.value = null;

                sandhillsRequest({
                    url: routes.UploadMedia,
                    method: 'post',
                    data: data,
                    errorHandling: {
                        showDetails: true,
                        useConsole: true,
                        useAlert: false
                    }
                }).then((response) => {
                    if (response && response.length > 0) {
                            let newMedia = [];
                            response.forEach(mediaResponse => {
                                if (mediaResponse.Success) {
                                    newMedia.push({
                                        src: '/Content/images/loading.gif',
                                        mediaId: mediaResponse.MediaId,
                                        mediaUrl: decodeURIComponent(mediaResponse.MediaUrl),
                                        fileName: mediaResponse.MediaFileName,
                                        loaded: false
                                    });
                                } else {
                                    setLoading(false);
                                    setMediaError(mediaResponse.Error != null && mediaResponse.Error != '' ? mediaResponse.Error : 'Error: Unable to upload media.');
                                    console.error(mediaResponse.Error != null && mediaResponse.Error != '' ? mediaResponse.Error : 'Error: Unable to upload media.');
                                }
                            });
                            let allMedia = [...newMedia];
                            setMedia(allMedia);
                            [...allMedia].forEach(data => loadMedia(data, 1));
                        }                   
                }).catch((error) => {
                    setMediaError('Error: Unable to upload media.');
                });
            }
        }
    };
    const handleDrop = useCallback((e) => {
        e.preventDefault();
        if (!props.disabled) {
            if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                onInputChange(e);
            }
        }
    }, []);

    const handleDragOver = useCallback((e) => {
        if (!props.disabled) {
            e.preventDefault();
        }
    }, []);

    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        if (!props.disabled) {
            setIsDragging(true);
        }
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        if (!props.disabled) {
            setIsDragging(false);
        }
    }, []);

    const handleClick = () => {
        if (!props.disabled) {
            fileInput?.current?.click();       
        }
    };

    return (
        <div className={`sng-image-upload ${props.containerClassName} ${isDragging ? 'dragging' : ''} ${props.disabled ? 'read-only' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onClick={handleClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick() } }}
            tabIndex={0}
            role="button">
            <div className='sng-image-upload-inner-div'>
                <div>
                    {loading && <LoadingRing sxBox={{ 'justify-content': 'center', 'height': '50px', 'margin-top': '20px', 'margin-bottom': '20px' }} />}
                    {mediaError && <div className='error'>{mediaError}</div>}
                {(media.length < maxPhotoUpload) ?
                    <div>
                        {!media?.length > 0 && <i className='far fa-image' />}
                        <div id='media-label'>{props.label}</div>
                        <input
                            multiple
                            type='file'
                            accept='image/*'
                            id={media.length > 0 ? '' : 'media'}
                            name={props.name}
                            className={props.inputClassName}
                            onChange={onInputChange}
                            style={{ display: 'none' }}
                            ref={fileInput}
                            aria-labelledby="media-label"
                            readOnly={props.disabled}
                        />
                    </div>
                    :
                    <>
                        <div className='error'>{mediaError}</div>
                        <div className='error'>{props.validationError}</div>
                        <div className='media-uploader'>
                            {media.length > 0 && media.map(data =>
                                <div key={data.mediaId} className='media-item'>
                                    <div className='media-container'>
                                        <div className='media-delete-item' aria-label={translator.translate('sandhills.delete')} role='button' tabIndex='0' onKeyDown={(e) => { if ((e.key === "Enter" || e.key === ' ') && !props.disabled) { e.preventDefault(); deleteMedia(data); } }} onClick={() => { if (!props.disabled) { deleteMedia(data); } }}>
                                            <i className='fas fa-x' />
                                        </div>
                                        <div className='media-filename-container'>
                                            <div className='media-filename-holder'>
                                                <div className='media-filename'>
                                                    {data.loaded ? <>{data.fileName}</> : <>Loading...</>}
                                                </div>
                                            </div>
                                        </div>
                                        <div className='media-image-container'>
                                            <div className='media-image'>
                                                {data.loaded &&
                                                    <div className='media-image-blur'>
                                                        <img
                                                            className='media-blurred-image'
                                                            src={data.src.replace('h=130', 'h=200').replace('w=130', 'w=250')}
                                                            mediaurl={data.mediaUrl}
                                                            filename={data.fileName}
                                                            alt={data.mediaId}
                                                        />
                                                    </div>}
                                                <img
                                                    className='media-display-image'
                                                    src={data.src.replace('h=130', 'h=200').replace('w=130', 'w=250')}
                                                    mediaurl={data.mediaUrl}
                                                    filename={data.fileName}
                                                    alt={data.mediaId}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>)}
                        </div>
                    </>}
                    </div>
                </div>
        </div>
    );
};

export default SerialNumMedia;